import {
  VIDEO_ALIGN,
  VIDEO_DEFAULT_ALIGN,
  VIDEO_DEFAULT_WIDTH_PERCENT,
  VIDEO_MAX_WIDTH_PERCENT,
  VIDEO_MIN_WIDTH_PERCENT,
  buildVideoDisplayHash,
  normalizeVideoAlign,
  normalizeVideoWidth,
  parseVideoDisplayHash,
  transcriptAttr,
  type VideoAlign,
} from "./video_display_hash";
import { normalizeEmbeddedVideoQuery } from "./video_embed_query";
import {
  YOUTUBE_PROVIDER,
  isYouTubeVideoId,
  parseYouTubeUrl,
  youtubeEmbedUrl,
  youtubeShareUrl,
} from "./youtube_video";

const LOOM_HOSTS = new Set(["loom.com", "www.loom.com"]);
const TELLA_HOSTS = new Set(["tella.tv", "www.tella.tv"]);
const LOOM_VIDEO_ID_RE = /^[A-Za-z0-9_-]{8,128}$/;
const TELLA_VIDEO_ID_RE = /^[A-Za-z0-9_-]{4,200}$/;
const CLIP_VIDEO_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{2,127}$/;
const LOOM_VIDEO_PATH_RE =
  /^\/(?:share|embed|i|looms\/share)\/([A-Za-z0-9_-]{8,128})\/?$/;
const TELLA_VIDEO_PATH_RE =
  /^\/video\/([A-Za-z0-9_-]{4,200})(?:\/(?:view|embed))?\/?$/;
const CLIP_VIDEO_PATH_RE =
  /^\/(?:clips|clip-public|embed\/clips)\/([A-Za-z0-9][A-Za-z0-9_-]{2,127})\/?$/;
const CLIP_VIDEO_URI_RE = /^clip:([A-Za-z0-9][A-Za-z0-9_-]{2,127})$/i;
const VIDEO_MARKDOWN_LINK_RE = /^\[([A-Za-z]+-Video)\]\(([^\s)]+)\)$/i;

export const VIDEO_PROVIDERS = {
  LOOM: "loom",
  TELLA: "tella",
  CLIP: "clip",
  YOUTUBE: YOUTUBE_PROVIDER,
} as const;

export type VideoProvider =
  (typeof VIDEO_PROVIDERS)[keyof typeof VIDEO_PROVIDERS];

export {
  VIDEO_ALIGN,
  VIDEO_DEFAULT_ALIGN,
  VIDEO_DEFAULT_WIDTH_PERCENT,
  VIDEO_MAX_WIDTH_PERCENT,
  VIDEO_MIN_WIDTH_PERCENT,
  normalizeVideoAlign,
  normalizeVideoWidth,
};
export type { VideoAlign };

export const LOOM_VIDEO_MIN_WIDTH_PERCENT = VIDEO_MIN_WIDTH_PERCENT;
export const LOOM_VIDEO_MAX_WIDTH_PERCENT = VIDEO_MAX_WIDTH_PERCENT;
export const LOOM_VIDEO_DEFAULT_WIDTH_PERCENT = VIDEO_DEFAULT_WIDTH_PERCENT;

export type EmbeddedVideoAttributes = {
  readonly provider: VideoProvider;
  readonly video_id: string;
  readonly width_percent: number;
  readonly embed_query: string;
  readonly align: VideoAlign;
  readonly has_transcript?: true;
};

export type LoomVideoAttributes = Omit<EmbeddedVideoAttributes, "provider">;

export { normalizeEmbeddedVideoQuery } from "./video_embed_query";

/** Returns a supported provider or the legacy Loom default. */
export function normalizeVideoProvider(value: unknown): VideoProvider {
  if (value === VIDEO_PROVIDERS.TELLA) return VIDEO_PROVIDERS.TELLA;
  if (value === VIDEO_PROVIDERS.CLIP) return VIDEO_PROVIDERS.CLIP;
  if (value === VIDEO_PROVIDERS.YOUTUBE) return VIDEO_PROVIDERS.YOUTUBE;
  return VIDEO_PROVIDERS.LOOM;
}

/** True when the provider-specific opaque video id is safe for a URL path. */
export function isEmbeddedVideoId(
  provider: VideoProvider,
  value: string,
): boolean {
  if (provider === VIDEO_PROVIDERS.TELLA) return TELLA_VIDEO_ID_RE.test(value);
  if (provider === VIDEO_PROVIDERS.CLIP) return CLIP_VIDEO_ID_RE.test(value);
  if (provider === VIDEO_PROVIDERS.YOUTUBE) return isYouTubeVideoId(value);
  return LOOM_VIDEO_ID_RE.test(value);
}

/** HTML data-attribute that carries the provider-specific video id. */
export function videoDataAttribute(provider: VideoProvider): string {
  if (provider === VIDEO_PROVIDERS.CLIP) return "data-page-clip-video";
  if (provider === VIDEO_PROVIDERS.TELLA) return "data-page-tella-video";
  if (provider === VIDEO_PROVIDERS.YOUTUBE) return "data-page-youtube-video";
  return "data-page-loom-video";
}

function attrsFromHash(
  provider: VideoProvider,
  videoId: string,
  hash: string,
  embedQuery: string,
): EmbeddedVideoAttributes {
  const display = parseVideoDisplayHash(hash);
  return {
    provider,
    video_id: videoId,
    width_percent: display.width_percent,
    embed_query: embedQuery,
    align: display.align,
    ...transcriptAttr(display.has_transcript),
  };
}

/**
 * Parses supported Loom / Tella / YouTube / own-clip URLs (and the canonical
 * `clip:` scheme) without trusting arbitrary iframe hosts.
 */
export function parseEmbeddedVideoUrl(
  value: string,
): EmbeddedVideoAttributes | null {
  const raw = value.trim();
  const [beforeHash, ...hashParts] = raw.split("#");
  const clipUriMatch = CLIP_VIDEO_URI_RE.exec(beforeHash ?? "");
  if (clipUriMatch?.[1]) {
    const hash = hashParts.length > 0 ? `#${hashParts.join("#")}` : "";
    return attrsFromHash(VIDEO_PROVIDERS.CLIP, clipUriMatch[1], hash, "");
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase();
  let provider: VideoProvider | null = null;
  if (TELLA_HOSTS.has(host)) provider = VIDEO_PROVIDERS.TELLA;
  else if (LOOM_HOSTS.has(host)) provider = VIDEO_PROVIDERS.LOOM;
  else if (CLIP_VIDEO_PATH_RE.test(url.pathname)) provider = VIDEO_PROVIDERS.CLIP;
  if (!provider) {
    const youtube = parseYouTubeUrl(raw);
    if (youtube === null) return null;
    return attrsFromHash(
      VIDEO_PROVIDERS.YOUTUBE,
      youtube.video_id,
      url.hash,
      youtube.embed_query,
    );
  }
  const pathMatch =
    provider === VIDEO_PROVIDERS.TELLA
      ? TELLA_VIDEO_PATH_RE.exec(url.pathname)
      : provider === VIDEO_PROVIDERS.CLIP
        ? CLIP_VIDEO_PATH_RE.exec(url.pathname)
        : LOOM_VIDEO_PATH_RE.exec(url.pathname);
  const videoId = pathMatch?.[1];
  if (!videoId || !isEmbeddedVideoId(provider, videoId)) return null;
  return attrsFromHash(
    provider,
    videoId,
    url.hash,
    normalizeEmbeddedVideoQuery(provider, url.search),
  );
}

/** Parses either a bare supported URL or canonical knowledge-page Markdown. */
export function parseEmbeddedVideoLine(
  value: string,
): EmbeddedVideoAttributes | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes("\n")) return null;
  const markdownMatch = VIDEO_MARKDOWN_LINK_RE.exec(trimmed);
  const parsed = parseEmbeddedVideoUrl(markdownMatch?.[2] ?? trimmed);
  if (!parsed) return null;
  if (
    markdownMatch?.[1] &&
    markdownMatch[1].toLowerCase() !==
      embeddedVideoMarkdownLabel(parsed.provider).toLowerCase()
  ) {
    return null;
  }
  return parsed;
}

/** Returns the stable Markdown label for a provider. */
export function embeddedVideoMarkdownLabel(provider: VideoProvider): string {
  if (provider === VIDEO_PROVIDERS.TELLA) return "Tella-Video";
  if (provider === VIDEO_PROVIDERS.CLIP) return "Clip-Video";
  if (provider === VIDEO_PROVIDERS.YOUTUBE) return "YouTube-Video";
  return "Loom-Video";
}

/** Returns the canonical share URL used in stored Markdown. */
export function embeddedVideoShareUrl(
  provider: VideoProvider,
  videoId: string,
  widthPercent: number,
  embedQuery = "",
  align: VideoAlign = VIDEO_DEFAULT_ALIGN,
  hasTranscript = false,
): string {
  const hash = buildVideoDisplayHash(widthPercent, align, hasTranscript);
  const encodedId = encodeURIComponent(videoId);
  if (provider === VIDEO_PROVIDERS.CLIP) return `clip:${encodedId}${hash}`;
  if (provider === VIDEO_PROVIDERS.YOUTUBE) {
    const share = youtubeShareUrl(videoId, embedQuery);
    return share.length === 0 ? "" : `${share}${hash}`;
  }
  const query = normalizeEmbeddedVideoQuery(provider, embedQuery);
  const querySuffix = query ? `?${query}` : "";
  const baseUrl =
    provider === VIDEO_PROVIDERS.TELLA
      ? `https://www.tella.tv/video/${encodedId}`
      : `https://www.loom.com/share/${encodedId}`;
  return `${baseUrl}${querySuffix}${hash}`;
}

/** Returns an allowlisted player URL for an iframe. */
export function embeddedVideoEmbedUrl(
  provider: VideoProvider,
  videoId: string,
  embedQuery = "",
): string {
  if (!isEmbeddedVideoId(provider, videoId)) return "about:blank";
  if (provider === VIDEO_PROVIDERS.YOUTUBE) {
    return youtubeEmbedUrl(videoId, embedQuery);
  }
  const encodedId = encodeURIComponent(videoId);
  if (provider === VIDEO_PROVIDERS.CLIP) return `/embed/clips/${encodedId}`;
  const query = normalizeEmbeddedVideoQuery(provider, embedQuery);
  const querySuffix = query ? `?${query}` : "";
  return provider === VIDEO_PROVIDERS.TELLA
    ? `https://www.tella.tv/video/${encodedId}/embed${querySuffix}`
    : `https://www.loom.com/embed/${encodedId}${querySuffix}`;
}

/** Serializes a video block to portable Markdown with persisted display width. */
export function renderEmbeddedVideoMarkdown(
  provider: VideoProvider,
  videoId: string,
  widthPercent: number,
  embedQuery = "",
  align: VideoAlign = VIDEO_DEFAULT_ALIGN,
  hasTranscript = false,
): string {
  if (!isEmbeddedVideoId(provider, videoId)) return "";
  return `[${embeddedVideoMarkdownLabel(provider)}](${embeddedVideoShareUrl(
    provider,
    videoId,
    widthPercent,
    embedQuery,
    align,
    hasTranscript,
  )})`;
}

export {
  isLoomVideoId,
  loomVideoEmbedUrl,
  loomVideoShareUrl,
  normalizeLoomVideoEmbedQuery,
  normalizeLoomVideoWidth,
  parseLoomVideoLine,
  parseLoomVideoUrl,
  renderLoomVideoMarkdown,
} from "./loom_video_legacy";
